import SdIconDark from 'assets/branding/SdIcon-dark.svg';
import SdIconLight from 'assets/branding/SdIcon-light.svg';
import SdLogoDark from 'assets/branding/SMARTdiagnostics-dark.svg';
import SdLogoLight from 'assets/branding/SMARTdiagnostics-light.svg';
import { MediaQueries } from 'custom_hooks/useWindowSize';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';

export const NavigationDrawerTransitionMs = 300;

/**
 * Shared active style for primary navigation items (used by NavLink activeStyle prop)
 */
export const NAV_ACTIVE_STYLE = {
    backgroundColor: 'var(--nav-secondary-selected)',
    color: 'var(--nav-text-selected)',
} as const;

/**
 * Active style for secondary navigation items (submenu items)
 * Uses subtle background instead of solid color
 */
export const NAV_SECONDARY_ACTIVE_STYLE = {
    backgroundColor: 'var(--nav-secondary-item-selected)',
} as const;

export const NavigationDrawerWrapper = styled.nav<{ $expanded: boolean }>`
    display: grid;
    grid-template-rows: min-content auto min-content;
    min-height: var(--nav-item-size);
    width: ${({ $expanded }) =>
        $expanded ? 'var(--nav-expanded-width)' : 'var(--nav-item-size)'};
    z-index: 30;
    overflow: hidden;
    color: var(--nav-text);
    background-color: var(--nav-primary-background);
    border-right: 0.1rem solid var(--nav-border);
    transition: width ${NavigationDrawerTransitionMs}ms ease-in-out;

    @media (prefers-reduced-motion) {
        transition: none;
    }

    @media ${MediaQueries.smallAndDown} {
        display: ${({ $expanded }) => ($expanded ? 'grid' : 'none')};
        width: var(--nav-expanded-width);
    }
`;

export const NavigationDrawerHeaderWrapper = styled.div<{ $expanded?: boolean }>`
    display: flex;
    min-height: 7rem;
    width: ${({ $expanded }) =>
        $expanded ? 'var(--nav-expanded-width)' : 'var(--nav-item-size)'};
    position: relative;
    justify-content: ${({ $expanded }) => ($expanded ? 'right' : 'center')};
    align-items: center;
    color: var(--nav-icon);
    cursor: pointer;
    transition: width ${NavigationDrawerTransitionMs}ms ease-in-out;

    &:hover {
        color: var(--nav-icon-hover);
        background-color: var(--nav-hover-background);
    }

    @media (prefers-reduced-motion) {
        transition: none;
    }

    @media ${MediaQueries.smallAndDown} {
        width: var(--nav-item-size);
        right: 0rem;
        background-color: transparent;
        color: var(--text-primary);

        &:hover {
            color: var(--text-primary);
        }
    }
`;

export const NavigationDrawerItems = styled.div`
    display: flex;
    flex-direction: column;
    overflow-x: hidden;
    overflow-y: auto;
`;

export const NavigationItemAccordion = styled.div`
    display: flex;
    flex-direction: column;
`;

export const NavigationItemIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--nav-item-size);
    height: var(--nav-item-size);
`;

export const NavigationItemLeftGroup = styled.div`
    display: flex;
    align-items: center;
`;

export const NavigationItemTitle = styled.span<{ $secondary?: boolean }>`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    font-weight: var(--font-weight-light);
    padding: ${({ $secondary }) => ($secondary ? '0 2rem' : '0rem')};
    height: ${({ $secondary }) => ($secondary ? 'var(--nav-item-size)' : '')};
    transition: all ${NavigationDrawerTransitionMs}ms ease-in-out;
    cursor: pointer;

    @media (prefers-reduced-motion) {
        transition: none;
    }
`;

export const NavigationItemHeader = styled.div`
    display: flex;
    width: 100%;
    min-height: var(--nav-item-size);
    align-items: center;
    justify-content: space-between;
    padding-right: 2.4rem;
`;

export const NavigationItemChevron = styled.span`
    color: var(--nav-chevron);

    .active & {
        color: var(--nav-text-selected);
    }
`;

export const SecondaryNavigationItemsWrapper = styled.div<{
    $show: boolean;
    $inPopup?: boolean;
}>`
    overflow: hidden;
    max-height: ${({ $show }) => ($show ? '100rem' : '0rem')};
    background-color: transparent;
    border-bottom: ${({ $show, $inPopup }) =>
        $show && !$inPopup ? '0.1rem solid var(--nav-border)' : 'none'};
    border-radius: ${({ $inPopup }) => ($inPopup ? '0 0 0.8rem 0.8rem' : '0')};
    transition: ${({ $show }) =>
        $show
            ? 'max-height 0.3s ease-in-out'
            : 'max-height 0.3s cubic-bezier(0, 1, 0, 1)'};

    @media (prefers-reduced-motion) {
        transition: none;
    }
`;

export const SecondaryNavigationItemHeader = styled(NavLink)`
    display: flex;
    height: 5rem;
    padding: 0rem 2.5rem;
    align-items: center;
    text-decoration: none;
    color: var(--nav-text);
    transition: all 0.3s ease-in-out;

    &:hover {
        background: var(--nav-hover-background);
        color: var(--nav-text);

        ${NavigationItemTitle} {
            transform: translateX(0.4rem) scale(1.01);
            transition: transform 0.3s ease-in-out;

            @media (prefers-reduced-motion) {
                transform: none;
                transition: none;
            }
        }

        @media (prefers-reduced-motion) {
            transition: none;
        }
    }

    @media (prefers-reduced-motion) {
        transition: none;
    }
`;

export const NavigationDrawerFooterWrapper = styled.div<{ $expanded: boolean }>`
    display: flex;
    flex-direction: row;
    width: ${({ $expanded }) =>
        $expanded ? 'var(--nav-expanded-width)' : 'var(--nav-item-size)'};
    height: ${({ $expanded }) =>
        $expanded ? 'unset' : 'calc(2 * var(--nav-item-size))'};
    background: transparent;
    transition: all 0.3s ease-in-out;

    @media (prefers-reduced-motion) {
        transition: none;
    }
`;

export const NavigationFooterHeader = styled.div`
    display: grid;
    min-height: var(--nav-item-size);
    align-items: center;
    grid-template-columns: 1fr var(--nav-item-size);
    color: var(--icon-default);
    cursor: pointer;
`;

export const NavigationFooterIcon = styled.div<{ $expanded: boolean }>`
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
    cursor: pointer;

    ${({ $expanded }) =>
        $expanded
            ? css`
                  width: 22rem;
                  height: 5rem;
                  margin-left: 0.5rem;
                  background-image: url('${SdLogoLight}');
                  transition: width 0.3s ease-in-out;

                  [data-theme='dark'] & {
                      background-image: url('${SdLogoDark}');
                  }

                  @media (prefers-reduced-motion) {
                      transition: none;
                  }
              `
            : css`
                  height: var(--nav-item-size);
                  width: var(--nav-item-size);
                  display: flex;
                  align-items: center;
                  justify-content: center;

                  &:hover {
                      background-color: var(--nav-hover-background);
                  }

                  &::after {
                      content: '';
                      display: block;
                      width: 2rem;
                      height: 2rem;
                      background-image: url('${SdIconLight.replaceAll("'", '%27')}');
                      background-size: contain;
                      background-repeat: no-repeat;
                      transform: scale(1.5);
                  }

                  [data-theme='dark'] &::after {
                      background-image: url('${SdIconDark.replaceAll("'", '%27')}');
                  }

                  transition: all 0.3s ease-in-out;

                  @media (prefers-reduced-motion) {
                      transition: none;
                  }
              `};
`;

export const NavigationChatbotIcon = styled.div<{ $expanded: boolean }>`
    cursor: pointer;
    background: transparent;
    color: var(--nav-icon);

    font-family: var(--font-family-base);
    font-weight: var(--font-weight-light);
    font-style: normal;

    ${({ $expanded }) =>
        $expanded
            ? css`
                  width: var(--nav-expanded-width);
                  height: var(--nav-item-size);
                  transition: width 0.3s ease-in-out;
                  border-right: 1px solid var(--nav-border);

                  align-items: center;
                  display: flex;
                  justify-content: align-start;
                  gap: 2rem;
                  padding-left: 2rem;

                  &:hover {
                      color: var(--nav-icon-hover);
                      background-color: var(--nav-hover-background);
                      ${NavigationItemTitle} {
                          transform: translateX(0.4rem) scale(1.01);
                          transition: transform ${NavigationDrawerTransitionMs}ms
                              ease-in-out;

                          @media (prefers-reduced-motion) {
                              transform: none;
                              transition: none;
                          }
                      }
                  }

                  @media (prefers-reduced-motion) {
                      transition: none;
                  }
              `
            : css`
                  height: var(--nav-item-size);
                  width: var(--nav-item-size);
                  display: flex;
                  align-items: center;
                  justify-content: center;

                  &:hover {
                      color: var(--nav-icon-hover);
                      background-color: var(--nav-hover-background);
                  }

                  transition: all 0.3s ease-in-out;

                  @media (prefers-reduced-motion) {
                      transition: none;
                  }
              `};
`;

export const NavigationFooterItems = styled.div<{ $show: boolean }>`
    overflow: hidden;
    max-height: ${({ $show }) => ($show ? '50rem' : '0rem')};
    transition: ${({ $show }) =>
        $show ? 'all 0.3s ease-in-out' : 'all 0.3s cubic-bezier(0, 1, 0, 1)'};

    @media (prefers-reduced-motion) {
        transition: none;
    }
`;

export const NavigationFooterContent = styled.div`
    display: flex;
    flex-direction: column;
    background: transparent;
`;

export const NavigationItem = styled(NavLink)<{
    $isDrawerExpanded?: boolean;
    $isExpanded?: boolean;
}>`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    text-decoration: none;
    min-height: var(--nav-item-size);
    transition-property: background, width;
    transition-timing-function: ease-in-out;
    color: var(--nav-text);
    background: ${({ $isExpanded }) =>
        $isExpanded ? 'var(--nav-hover-background)' : 'transparent'};
    transition-duration: ${({ $isDrawerExpanded }) =>
        $isDrawerExpanded ? '0.5s' : 'none'};

    &:hover {
        background: var(--nav-hover-background);
        transition-duration: ${({ $isDrawerExpanded }) =>
            $isDrawerExpanded ? '0.3s' : 'none'};

        @media (prefers-reduced-motion) {
            transition: none;
        }
    }

    @media (prefers-reduced-motion) {
        transition: none;
    }
`;
